import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Initial state
const initialState = {
  data: null,
  loading: false,
  error: null,
  year: new Date().getFullYear(),
  // The same report for year - 1, used only by the headline comparisons
  // (design 31a). Deliberately has no `loading` or `error` of its own: it must
  // never gate the page, and a failure degrades a comparison rather than
  // showing an error for a figure nobody asked to see.
  previousYearData: null
};

// Async thunks
export const fetchKeyMetrics = createAsyncThunk(
  'keyMetrics/fetchKeyMetrics',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().keyMetrics;
      const response = await axios.get('/v1/reports/key-metrics', {
        params: { year }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch Key Metrics report'
      );
    }
  }
);

// The previous year of the SAME report, for the headline comparisons. Resolves
// to null on any failure rather than rejecting: the page must render without it.
export const fetchPreviousYearKeyMetrics = createAsyncThunk(
  'keyMetrics/fetchPreviousYear',
  async (_, { getState }) => {
    try {
      const { year } = getState().keyMetrics;
      const response = await axios.get('/v1/reports/key-metrics', {
        params: { year: year - 1 }
      });
      return response.data;
    } catch {
      return null;
    }
  }
);

// Slice
const keyMetricsSlice = createSlice({
  name: 'keyMetrics',
  initialState,
  reducers: {
    setYear: (state, action) => {
      state.year = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKeyMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeyMetrics.fulfilled, (state, action) => {
        state.loading = false;
        // Thunks are not cancelled, so two quick year changes can resolve out
        // of order. The payload names its own year; anything else is stale.
        if (action.payload?.year !== undefined && action.payload.year !== state.year) return;
        state.data = action.payload;
      })
      .addCase(fetchKeyMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Clear the old comparison the moment the year changes, so a card can
      // never show this year's figure against last year's neighbour.
      .addCase(fetchPreviousYearKeyMetrics.pending, (state) => {
        state.previousYearData = null;
      })
      .addCase(fetchPreviousYearKeyMetrics.fulfilled, (state, action) => {
        // Same race: a slow response for an older year must not become the
        // comparison for the year now on screen, which would silently show a
        // year against itself.
        if (action.payload?.year !== undefined && action.payload.year !== state.year - 1) return;
        state.previousYearData = action.payload;
      })
      .addCase(fetchPreviousYearKeyMetrics.rejected, (state) => {
        state.previousYearData = null;
      });
  }
});

// Actions
export const { setYear, clearError } = keyMetricsSlice.actions;

// Selectors
export const selectKeyMetricsData = (state) => state.keyMetrics.data;
export const selectKeyMetricsLoading = (state) => state.keyMetrics.loading;
export const selectKeyMetricsError = (state) => state.keyMetrics.error;
export const selectKeyMetricsYear = (state) => state.keyMetrics.year;
export const selectPreviousYearData = (state) => state.keyMetrics.previousYearData;

export default keyMetricsSlice.reducer;
